import React, { useState, useEffect } from 'react'
import { OpsChatSession } from '@/store/slices/opsChatSlice'
import { MoreVertical, Edit2, Trash2, BarChart3, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface OpsConversationItemProps {
  session: OpsChatSession
  isActive: boolean
  isLoading?: boolean
  onSelect: (sessionId: string) => void
  onRename: (sessionId: string, newTitle: string) => Promise<void>
  onDelete: (sessionId: string) => Promise<void>
}

interface ConfirmDeleteModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  isDeleting: boolean
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  isDeleting
}) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border rounded-lg p-6 w-full max-w-md mx-4 shadow-lg">
        <h3 className="text-lg font-semibold mb-2">Delete Conversation</h3>
        <p className="text-muted-foreground mb-4">
          Are you sure you want to delete "{title}"? This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </div>
      </div>
    </div>
  )
}

const OpsConversationItem: React.FC<OpsConversationItemProps> = ({
  session,
  isActive,
  isLoading = false,
  onSelect,
  onRename,
  onDelete
}) => {
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(session.title || '')
  const [showMenu, setShowMenu] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)

  const displayTitle = session.title || 'Ops Analysis'
  const messageCount = session.messages?.length || 0
  const lastMessageTime = session.updatedAt ? new Date(session.updatedAt) : null

  const formatTime = (date: Date) => {
    const now = new Date()
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60))
    
    if (diffInMinutes < 1) return 'Just now'
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`
    if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d ago`
    return date.toLocaleDateString()
  }

  const handleSelect = () => {
    if (!isLoading && !isDeleting) {
      onSelect(session.id)
    }
  }

  const handleRename = async () => {
    if (editTitle.trim() && editTitle !== session.title) {
      setIsRenaming(true)
      try {
        await onRename(session.id, editTitle.trim())
        setIsEditing(false)
      } catch (error) {
        console.error('Failed to rename session:', error)
        setEditTitle(session.title || '')
      } finally {
        setIsRenaming(false)
      }
    } else {
      setIsEditing(false)
      setEditTitle(session.title || '')
    }
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await onDelete(session.id)
      setShowDeleteModal(false)
    } catch (error) {
      console.error('Failed to delete session:', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRename()
    } else if (e.key === 'Escape') {
      setIsEditing(false)
      setEditTitle(session.title || '')
    }
  }

  return (
    <div className={`
      group relative p-3 rounded-md transition-all duration-200 cursor-pointer
      ${isActive 
        ? 'bg-primary/10 border-l-2 border-l-primary' 
        : 'hover:bg-muted/50'
      }
      ${(isLoading || isDeleting) ? 'opacity-50 cursor-not-allowed' : ''}
    `}>
      {/* Loading indicator */}
      {(isLoading || isDeleting) && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/50 rounded-md">
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {/* Main content area */}
      <div onClick={handleSelect} className="flex-1">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2 flex-1 min-w-0">
            <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-3 h-3 text-primary" />
            </div>
            
            <div className="min-w-0 flex-1">
              {isEditing ? (
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onBlur={handleRename}
                  onKeyDown={handleKeyPress}
                  className="text-sm font-medium bg-background border border-input rounded px-2 py-1 w-full focus:outline-none focus:ring-2 focus:ring-ring"
                  autoFocus
                  disabled={isRenaming}
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <h4 className="text-sm font-medium truncate">
                  {displayTitle}
                </h4>
              )}
              
              <div className="flex items-center space-x-2 mt-1">
                {lastMessageTime && (
                  <span className="text-xs text-muted-foreground">
                    {formatTime(lastMessageTime)}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Menu button */}
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowMenu(!showMenu)
              }}
              className="p-1 rounded-md opacity-0 group-hover:opacity-100 hover:bg-muted transition-all duration-200"
              disabled={isLoading || isDeleting}
            >
              <MoreVertical className="w-3 h-3" />
            </button>

            {/* Dropdown menu */}
            {showMenu && (
              <>
                {/* Backdrop */}
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowMenu(false)}
                />
                
                {/* Menu */}
                <div className="absolute right-0 top-6 z-20 bg-popover border border-border rounded-md shadow-lg py-1 min-w-[120px]">
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsEditing(true)
                      setShowMenu(false)
                    }}
                    className="w-full px-3 py-2 text-left text-sm hover:bg-muted transition-colors flex items-center space-x-2"
                    disabled={isRenaming}
                  >
                    <Edit2 className="w-3 h-3" />
                    <span>Rename</span>
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setShowMenu(false)
                      setShowDeleteModal(true)
                    }}
                    className="w-full px-3 py-2 text-left text-sm text-destructive hover:bg-destructive/10 transition-colors flex items-center space-x-2"
                    disabled={isDeleting}
                  >
                    <Trash2 className="w-3 h-3" />
                    <span>Delete</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Session error */}
      {session.error && (
        <div className="mt-2 text-xs text-destructive bg-destructive/10 rounded px-2 py-1">
          {session.error}
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title={session.title || 'this conversation'}
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default OpsConversationItem